import { env } from "@/web/lib/env";

/**
 * The founder's letter, split into sentence-per-line groups. Each group is a
 * paragraph; each sentence renders on its own line. Larger gap separates groups.
 */
const STORY_GROUPS: string[][] = [
  [
    "My story is my family's story.",
    "And my family's story is an American one.",
    "Maybe you've heard it before.",
    "It goes like this.",
  ],
  [
    "Immigrant parents leave home, in hopes of giving their kids a better one.",
    "They land in New York to make a new life.",
    "They start a small business built on big dreams.",
    "They work relentlessly until they have regulars.",
    "They sponsor little league and give young moms a little help when they're in a pinch.",
    "All these little things make a big difference on their block.",
    "But their neighborhood changes fast. The rent changes faster.",
    "The family puts up a fight, but it isn't a fair one.",
    "Their small grocery store is replaced by a bank chain.",
    "And their big dreams are replaced by a new reality.",
  ],
  [
    "It's a story as old as Main Street America.",
    "And once big business lays eyes on a particular property, it's a relatively short one after that.",
    "Happening seemingly overnight, like scaffolding going up around the local corner store.",
    "And for every family like mine in Brooklyn, there's countless others, from Orlando to Oakland,",
    "Who have watched the fruits of their labor be picked by someone with deeper pockets.",
  ],
  [
    "People in this country still like to romanticize small businesses like my parents'",
    "Believing success and longevity still go to the hardest worker, not the highest bidder.",
    "But if you've met my parents, or any small business owner for that matter,",
    "You'd know that a lack of work ethic has never been the problem.",
    "The problem is a system that doesn't reward, but punishes entrepreneurs,",
    "Cashing out the value they create in our neighborhoods to whoever pays the most.",
  ],
  [
    "Whether you're the son of immigrants who've experienced its effects firsthand like I have,",
    "Or just someone who has watched the gridded streets of their once-vibrant neighborhood start to look and feel like aisles in one big mall,",
    "Gentrification can feel inevitable.",
    "And that feeling of inevitability can quickly turn to indifference.",
    "And once that happens we've lost our chance at change.",
    "And the dream is dead.",
  ],
  [
    "I started WITH.CO to do everything I can to make sure that doesn't happen.",
    "And to ensure the next generation can dream the way my parents did on distant shores.",
    "Even in a system that is rigged against them.",
    "It's what keeps me up at night, and gets me out of bed in the morning,",
    "Defining me more than I care to admit sometimes.",
    "And while my family's story certainly isn't a novel one,",
    "The experience, along with a lot of help and learning since, has inspired what I believe could be a novel solution.",
    "A simple approach to combat a problem that's anything but.",
  ],
  [
    "Here's how it works.",
    "We find small businesses people already believe in.",
    "The corner stores. The theaters. The restaurants that make a block feel like a neighborhood.",
    "Then we help them own the buildings they operate out of with the people who know them best.",
    "Customers. Neighbors. Regulars. Believers.",
    "They invest. The business gets a permanent home. The community gets a real stake in the place it helped make valuable.",
    "Not charity. Real ownership.",
  ],
  [
    "And so far it's working.",
    "We've worked with over twenty thousand businesses.",
    "And we believe this is just the beginning.",
  ],
  [
    "I said above that this solution is a relatively simple one.",
    "But make no mistake, simple doesn't mean easy.",
    "It will take businesses who believe in what we're doing, and the importance and urgency of it.",
    "It will take a heroic effort from our growing team.",
    "More Alexs, Ferns, Reillys and Stus who are willing to fight like hell in order to give these small businesses a fighting chance.",
    "To take control of their own businesses, and in the process, their own destinies.",
    "It will take a renewed belief from all of us, about what's possible when we play on equal footing. When we remember that the strength of a community truly does lie in its corner stores.",
    "And that the heart of the city has never been where the buildings are the highest.",
    "It's always been where the people care the most, and dream the biggest.",
  ],
];

/**
 * The founder's open letter, presented as a white card framed by a thick white
 * border and soft shadow: a 4:3 photo up top, the letter body, and a signed
 * sign-off (photo + handwritten name/title).
 */
export function AboutStory() {
  return (
    <section className="w-full bg-withus-white px-6 pb-20 md:px-12 md:pb-28">
      <div className="mx-auto flex max-w-[850px] flex-col gap-10 border-11 border-withus-pure-white bg-withus-pure-white p-4 shadow-withus-default md:p-10">
        <img
          src={`${env.VITE_WITHCO_IMAGE_CDN_BASE}/song-parents.png`}
          alt="Kevin Song with his family"
          className="aspect-1200/900 w-full object-cover"
        />

        <div className="flex flex-col gap-6 text-base leading-[26px] text-withus-dark-blue">
          {STORY_GROUPS.map((sentences, idx) => (
            <p key={idx}>
              {sentences.map((sentence, sIdx) => (
                <span key={sIdx} className="block">
                  {sentence}
                </span>
              ))}
            </p>
          ))}
        </div>

        <div className="flex items-center gap-5">
          <img
            src={`${env.VITE_WITHCO_IMAGE_CDN_BASE}/ks-headshot.png`}
            alt="Kevin Song"
            className="size-[70px] rounded-full object-cover"
          />
          <div className="flex flex-col font-signature text-[37px] leading-7 text-withus-dark-blue">
            <span>Kevin Song</span>
            <span>Founder, CEO</span>
          </div>
        </div>
      </div>
    </section>
  );
}
